import { useRecoilValue, useSetRecoilState } from "recoil"
import { sidebarTaskState, quadriProjectIdState, projectIdState, projectOriginState } from "../StateSelectors"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import TasksActivities from "./TasksActivities"
import { GET_TASKS } from "../../api/QuadriApi"
import { TASKS_CHUNK_SIZE, TaskListTask, TasksDisplaySidebar, scrollElementId } from "../TasksDisplaySidebar"
import "./Tasks.css"
import { PublishedTaskPreview } from "./PublishedTaskPreview"

enum ActiveTab {
  TaskDetails = 0,
  Activity = 1
}

const TasksSidebar = () => {
  const [tabActive, setActiveTab] = useState(ActiveTab.TaskDetails)

  const setSidebar = useSetRecoilState(sidebarTaskState)
  const quadriProjectId = useRecoilValue(quadriProjectIdState)
  const projectId = useRecoilValue(projectIdState)
  const projectOrigin = useRecoilValue(projectOriginState)

  const { t } = useTranslation(["tasks", "common"])

  if (!quadriProjectId) throw new Error(t("tasksSidebar.noQuadriProject"))
  if (!projectId) throw new Error(t("tasksSidebar.noConnectProject"))

  // Info about the task in the main grid whose subtasks are being shown in the sidebar
  const taskInfo = useRecoilValue(sidebarTaskState) ?? null

  const taskName = taskInfo ? taskInfo.taskName : t("tasksSidebar.noName")
  const guid = taskInfo ? (taskInfo.guid ? taskInfo.guid : "") : ""
  const id = taskInfo ? taskInfo.taskId : ""
  const include_deleted = taskInfo ? taskInfo.includeDeleted : false

  const subTasks = taskInfo?.subTaskIds || []
  const getTaskIdsSlice = (startIdx: number) =>
    subTasks.slice(startIdx, Math.min(startIdx + TASKS_CHUNK_SIZE, subTasks.length))

  return (
    <div className="panel">
      <header>
        <div className="panel-title">
          <h3>{taskName}</h3>
        </div>
        <button className="button icon-circle icon-medium tertiary" onClick={() => setSidebar(null)}>
          <i className="icon-font tc-icon-content-close" />
        </button>
      </header>
      <section id={scrollElementId}>
        <div className="panel-body">
          {taskInfo?.taskTag === "PRESENT_AS_TRIMBIM_TOOL" && (
            <PublishedTaskPreview
              projectId={projectId}
              projectOrigin={projectOrigin}
              quadriProjectId={quadriProjectId}
              taskId={taskInfo?.taskId}
            />
          )}
          {/* tabs section*/}
          <ul className="tabs no-gap">
            <li
              className={`tabs-title text-ellipsis ${tabActive === ActiveTab.TaskDetails && "active"}`}
              onClick={() => setActiveTab(ActiveTab.TaskDetails)}
            >
              <span className="text-ellipsis px-1">{t("tasksSidebar.details")}</span>
            </li>
            <li
              className={`tabs-title text-ellipsis" ${tabActive === ActiveTab.Activity && "active"}`}
              onClick={() => setActiveTab(ActiveTab.Activity)}
            >
              <span className="text-ellipsis px-1">{t("tasksSidebar.activity")}</span>
            </li>
          </ul>
          <div className="tabs-content">
            {/* Task details */}
            <div className={`tabs-panel ${tabActive === ActiveTab.TaskDetails && "active"}`}>
              <div>
                <div className="pb-1">
                  <div className="label-group ml-2">
                    <label>{t("tasksSidebar.created")}</label>
                    <div className="value">{taskInfo?.createdData}</div>
                  </div>
                  <div className="label-group ml-2">
                    <label>{t("tasksSidebar.latestChange")}</label>
                    <div className="value">{taskInfo?.lastModifiedData}</div>
                  </div>
                  <div className="label-group ml-2">
                    <label>{t("sidebar.internalId", { ns: "common" })}</label>
                    <div className="value">{id}</div>
                  </div>
                  {guid && (
                    <div className="label-group ml-2">
                      <label>GUID</label>
                      <div className="value">{guid}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="sub-section">
                <div className="section-header">
                  <h5>
                    {t("tasksSidebar.subTasks")} ({taskInfo?.subTaskIds?.length || 0})
                  </h5>
                </div>
                <TasksDisplaySidebar
                  nTasksTotal={subTasks.length}
                  quadriProjectId={quadriProjectId}
                  scrollElementId={scrollElementId}
                  include_deleted={include_deleted}
                  getTaskIdsSlice={getTaskIdsSlice}
                  gqlQuery={GET_TASKS}
                  getTaskStatus={getTaskStatus}
                />
              </div>
            </div>
            {/* End of Task details, start of Task activities */}
            <div className={`tabs-panel ${tabActive === ActiveTab.Activity && "active"}`}>
              <TasksActivities projectId={projectId} taskId={taskInfo?.taskId} />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

const getTaskStatus = (task: TaskListTask) => (task.deleted ? "DELETED" : undefined)

export default TasksSidebar
